// Integrations & Hardware Hub — single source of truth.
// External URLs are intentionally centralized so affiliate/partner links
// can be swapped in one place once official partner status is finalized.

const LOGO_BASE = `${process.env.PUBLIC_URL || ''}/integration-logos`;

export const POS_PLATFORMS = [
  {
    id: 'shopify',
    name: 'Shopify POS',
    logo: `${LOGO_BASE}/shopify.svg`,
    logoAlt: 'Shopify POS logo',
    targetVenue: 'QSRs, Cafes, Food Retail',
    cost: 'From $0/mo (Lite) · $89 USD (Pro)',
    syncType: '2-Way Menu & Order Injection',
    status: 'Active',
    url: 'https://www.shopify.com/ca/pos',
    bestFor: 'Quick-service restaurants, cafes, and food retailers already selling on Shopify who want online orders unified with in-store sales.',
    highlights: [
      '2-way menu sync — update items once, everywhere',
      'Online orders injected directly into Shopify orders',
      'Interac, Visa, Mastercard & Amex compatible in-store',
      'Canadian GST/PST/HST tax settings mirrored automatically',
    ],
  },
  {
    id: 'lightspeed',
    name: 'Lightspeed POS',
    logo: `${LOGO_BASE}/lightspeed.svg`,
    logoAlt: 'Lightspeed Restaurant POS logo',
    targetVenue: 'Full-Service, Multi-Room Hospitality',
    cost: 'Custom Tiers (~$89–$189 CAD)',
    syncType: 'Live API & Table Mapping',
    status: 'Active',
    url: 'https://www.lightspeedhq.ca/pos/restaurant/',
    bestFor: 'Full-service restaurants and multi-room hospitality venues that need live table mapping and deep floor-plan control.',
    highlights: [
      'Live API connection with table & course mapping',
      'Online orders route into your existing service flow',
      'Interac and all major Canadian card types supported',
      'Province-based GST/PST/HST configurations respected',
    ],
  },
  {
    id: 'square',
    name: 'Square POS',
    logo: `${LOGO_BASE}/square.svg`,
    logoAlt: 'Square for Restaurants POS logo',
    targetVenue: 'Fast Casual, Food Trucks, Cafes',
    cost: '$0/mo (Free) · Plus from $49/mo',
    syncType: 'Direct Order Routing',
    status: 'Active',
    url: 'https://squareup.com/ca/en/point-of-sale/restaurants',
    bestFor: 'Fast-casual spots, food trucks, and cafes that want a free-tier POS with dead-simple direct order routing.',
    highlights: [
      'Direct order routing into Square for Restaurants',
      'Free tier available — start with zero software cost',
      'Interac debit + tap-to-pay fully supported in Canada',
      'Automatic Canadian tax mirroring by province',
    ],
  },
  {
    id: 'clover',
    name: 'Clover POS',
    logo: `${LOGO_BASE}/clover.svg`,
    logoAlt: 'Clover POS logo',
    targetVenue: 'High-Volume Counter Service, Bakeries',
    cost: 'Starter $79+ CAD/mo',
    syncType: 'Cloud API Order & Inventory Sync',
    status: 'Active',
    url: 'https://www.clover.com/en-CA',
    bestFor: 'High-volume counter-service restaurants and bakeries running Clover hardware who want cloud-level order and inventory sync.',
    highlights: [
      'Cloud API order injection + inventory sync',
      '2-way menu updates between Clover and Hungry Canadian',
      'Interac, chip & PIN, and contactless supported',
      'GST/PST/HST handled per your Clover tax setup',
    ],
  },
  {
    id: 'deliverect',
    name: 'Deliverect',
    logo: `${LOGO_BASE}/deliverect.svg`,
    logoAlt: 'Deliverect middleware aggregator logo',
    targetVenue: 'Ghost Kitchens & Delivery Aggregators',
    cost: 'Custom Volume Plans',
    syncType: 'Centralized Marketplace Routing',
    status: 'Active',
    url: 'https://www.deliverect.com/',
    bestFor: 'Ghost kitchens and multi-marketplace operators who aggregate every delivery channel into one central order stream.',
    highlights: [
      'Centralized routing across every delivery marketplace',
      'Hungry Canadian orders join your unified Deliverect queue',
      'Works alongside virtually any POS Deliverect supports',
      'Canadian tax configurations passed through untouched',
    ],
  },
];

export const HARDWARE = [
  {
    id: 'epson',
    name: 'Epson Thermal Printers',
    badge: 'Bluetooth & Wi-Fi',
    details:
      'Compatible with Epson mSeries & OmniLink wireless thermal receipt printers via local Wi-Fi or Bluetooth pairing.',
    benefit: 'Reliable local counter printing; zero cables needed with Bluetooth/Wi-Fi setups.',
  },
  {
    id: 'star-micronics',
    name: 'Star Micronics CloudPRNT',
    badge: 'Cloud-to-Printer',
    details:
      'Native integration with Star Micronics CloudPRNT technology (e.g., mC-Print3, TSP100IV series).',
    benefit:
      'Direct cloud-to-printer order firing without requiring a secondary tablet or local PC controller on-site.',
  },
];

export const STRIPE_URL = 'https://stripe.com/en-ca';
export const STRIPE_LOGO = `${LOGO_BASE}/stripe.svg`;

export const INTEGRATIONS_FAQ = [
  {
    id: 'stripe-payouts',
    q: 'Do I need to create a Stripe account to receive order payouts?',
    a: 'Yes. To receive automated payouts for online orders paid via debit or credit cards, each merchant must create or connect an existing Stripe account with Hungry Canadian during onboarding.',
  },
  {
    id: 'printer-workflow',
    q: 'How do Star Micronics CloudPRNT and Epson printers work with Hungry Canadian?',
    a: 'Hungry Canadian communicates directly with Epson Bluetooth/Wi-Fi printers on your local network or wirelessly pushes print jobs to Star Micronics CloudPRNT printers over the internet, triggering receipt printing as soon as an order is placed.',
  },
  {
    id: 'pos-live-today',
    q: 'Are these POS integrations live today?',
    a: 'Yes, all 5 POS/aggregator integrations (Shopify, Lightspeed, Square, Clover, Deliverect) are active and ready for live deployment.',
  },
  {
    id: 'canadian-taxes',
    q: 'How are Canadian taxes (GST, PST, HST) calculated?',
    a: 'Hungry Canadian automatically mirrors the exact tax configurations set up inside your POS or Stripe account based on your Canadian province.',
  },
];

const JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://merchants.hungrycanadian.com/integrations/#webpage',
      url: 'https://merchants.hungrycanadian.com/integrations/',
      name: 'POS, Stripe & Thermal Printer Integrations for Canadian Restaurants',
      description:
        'Connect Hungry Canadian with Shopify POS, Lightspeed, Square, Clover, Deliverect, Stripe payments, and Epson or Star Micronics thermal printers.',
    },
    {
      '@type': 'ItemList',
      name: 'Supported POS, Gateway & Hardware Integrations',
      itemListElement: [
        {
          '@type': 'SoftwareApplication',
          position: 1,
          name: 'Stripe Connect Payments',
          applicationCategory: 'PaymentApplication',
          operatingSystem: 'Web',
          description:
            'Powers direct bank payouts and online credit/debit card processing for Canadian restaurants.',
        },
        {
          '@type': 'SoftwareApplication',
          position: 2,
          name: 'Shopify POS for Restaurants',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'iOS, Android',
        },
        {
          '@type': 'SoftwareApplication',
          position: 3,
          name: 'Lightspeed Restaurant POS',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'iOS, Web',
        },
        {
          '@type': 'SoftwareApplication',
          position: 4,
          name: 'Square for Restaurants',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'iOS, Android',
        },
        {
          '@type': 'SoftwareApplication',
          position: 5,
          name: 'Clover POS for Restaurants',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Clover OS, Android',
        },
        {
          '@type': 'SoftwareApplication',
          position: 6,
          name: 'Deliverect Middleware Aggregator',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
        },
        {
          '@type': 'SoftwareApplication',
          position: 7,
          name: 'Epson Bluetooth & Wi-Fi Thermal Printers',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Local network / Bluetooth / Wi-Fi',
          description: 'Wireless kitchen and counter printing via local network or Bluetooth.',
        },
        {
          '@type': 'SoftwareApplication',
          position: 8,
          name: 'Star Micronics CloudPRNT Printers',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'CloudPRNT (cloud-to-printer)',
          description:
            'Direct cloud-to-printer thermal receipt printing without local server hardware.',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: INTEGRATIONS_FAQ.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  ],
};

// Escape `<` so the serialized JSON can never break out of the <script> tag.
export const INTEGRATIONS_JSON_LD_STRING = JSON.stringify(JSON_LD).replace(/</g, '\\u003c');
